import React, { useEffect, useState } from 'react';
import { Card, CardContent, Drawer, Grid, useMediaQuery } from '@material-ui/core';
import { useGlobal, Header, NavLink, Typography } from '@rcg/ui-react';
import { useReactiveVar, useQuery } from '@apollo/client';
import clsx from 'clsx';
import { navigate } from '@gatsbyjs/reach-router';
import { globalizationPath } from '../../utils/globalization/globalizationPath';
import Button from '../UiComponents/Button';
import ProgressLineItems from '../ProgressLine';
import Theme from '../../utils/theme';
import useStyles from './useStyles';
import useStylesResults from '../Results/useStyles';
import Dictionary from '../../utils/dictionary';
import ResultsSuccess from '../ResultsSuccess';
import ResultsFail from '../ResultsFail';
import FiltersPanel from '../FiltersPanel';
import InfoTooltip from '../InfoTooltip';
import AirLoader from '../AirLoader';
import {
  filteredResultsVar,
  searchResultsVar,
  selectedDepartureVar,
  browseParamsVar,
  searchParamsVar,
  bookingProfileVar,
} from '../../operations/localState/reactiveVariables';
import { LAUNCH_DARKLY_FLAGS_AIR } from '../../operations/queries';
import useBrowseFlightsSearch from '../../operations/dataAccess/useBrowseFlightsSearch';
import useFlightSearchReturn from '../../operations/dataAccess/useFlightSearchReturn';
import useCruisePackageCode from '../../operations/dataAccess/useCruisePackageCode';
import useCruiseInfo from '../../operations/dataAccess/useCruiseInfo';
import {
  APPLICATION_NAME,
  FLIGHT_SEARCH_TYPE,
  NAVIGATION_PATHS,
  SESSION_STORAGE_LAST_DEPARTURE_FLIGHT_RESULTS,
  SESSION_STORAGE_NO_FLIGHTS_RESULTS_ERROR,
  ENABLE_ONE_WAY_FLAG,
  SESSION_STORAGE_FLIGHT_TYPE_CODE,
  SESSION_STORAGE_NAVIGATION_HISTORY_LAST_ENTRY,
  SESSION_STORAGE_LAST_BROWSE_PARAMS,
  SUPPORT_PHONE_NUMBER_LD_FLAG,
} from '../../utils/constants';
import { clearFilters } from '../../operations/localState/setFilters';
import setSearchResults from '../../operations/localState/setSearchResults';

import {
  setSessionStorageItem,
  getSessionStorageItem,
} from '../../operations/sessionStorage/utils';
import InternalError from '../InternalError';
import FlightSearchForm from '../FlightSearchForm';
import {
  trackDepartureResultsView,
  trackReturnResultsView,
} from '../../utils/analytics/browseTracking';
import { setBookingProfile, setBookingToken } from '../../operations/localState/setBookingProfile';
import { setBrowseParams } from '../../operations/localState/setSearchParams';
import ReviewCard from '../ReviewCard';
import useDictionary from '../../hooks/useDictionary';
import NotificationBannerComponent from '../NotificationBanner';
import useCommonStyles from '../../utils/useCommonStyles';
import { getGuests } from '../../utils/payment';
import { LDUser } from '../../utils/ldUser';
import getBrand from '../../utils/getBrand';
import ItineraryDetails from '../ItineraryDetails';

const BrowseResults = ({ flightType, urlParams }) => {
  const {
    renderContent,
    data: { lang, market, country },
  } = useGlobal();
  const getDictionaryValue = useDictionary(Dictionary);
  const urlPath = globalizationPath(market, lang, NAVIGATION_PATHS.base);

  const classes = useStyles();
  const styles = useCommonStyles();
  const classesResults = useStylesResults();

  const [lastSearchDate, setLastSearchDate] = useState(null);
  const [nearestAirport, setNearestAirport] = useState('');
  const [minDate, setMinDate] = useState('');
  const [maxDate, setMaxDate] = useState('');
  const [arrivalDateState, setArrivalDateState] = useState(null);
  const [airports, setAirports] = useState([]);
  const [departureAirportCode, setDepartureAirportCode] = useState('');
  const [isInternalServerError, setIsInternalServerError] = useState(false);
  const [departureDateState, setDepartureDateState] = useState(null);
  const [shipCodeState, setShipCodeState] = useState('none');
  const [canOpenFiltersDrawer, setCanOpenFiltersDrawer] = useState(false);
  const [flightDatesBrowse, setFlightDatesBrowse] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [showNoResultsError, setShowNoResultsError] = useState(false);
  const [clearResults, setClearResults] = useState(true);
  const [errorBannerTitle, setErrorBannerTitle] = useState(
    getDictionaryValue(Dictionary.airFlightBookFlightsReservationErrorTechnicalIssuesTitle.key),
  );
  const [errorBannerMessage, setErrorBannerMessage] = useState(
    getDictionaryValue(
      Dictionary.airFlightBookFlightsReservationErrorTechnicalIssuesDescription.key,
    ),
  );

  const shouldHideFiltersDrawer = useMediaQuery(Theme.breakpoints.up('lg'));
  const filteredResults = useReactiveVar(filteredResultsVar);
  const selectedDeparture = useReactiveVar(selectedDepartureVar);
  const searchResults = useReactiveVar(searchResultsVar);
  const browseParams = useReactiveVar(browseParamsVar);
  const isSm = useMediaQuery(Theme.breakpoints.down('sm'));
  const isXl = useMediaQuery(Theme.breakpoints.up('xl'));
  const isXlDictionaries =
    isXl || isSm
      ? Dictionary.airSearchFlightsLabel.defaultValue
      : Dictionary.airSearchLabel.defaultValue;
  const isXlDictionariesKey =
    isXl || isSm ? Dictionary.airSearchFlightsLabel.key : Dictionary.airSearchLabel.key;
  let searchCriteria = null;
  if (browseParams && browseParams.searchCriteria) {
    searchCriteria = browseParams.searchCriteria;
  }

  const [numberOfGuestsCounter, setNumberOfGuestsCounter] = useState(
    (searchCriteria && searchCriteria.numberOfGuests) || 2,
  );

  const { data: launchDarklyFlags } = useQuery(LAUNCH_DARKLY_FLAGS_AIR, {
    fetchPolicy: 'no-cache',
    variables: {
      ldUser: LDUser(),
      language: lang,
      clientApp: APPLICATION_NAME,
      application: APPLICATION_NAME,
      brand: getBrand().ldBrand,
      bookingCountry: country,
    },
  });

  const [execSearchPackageCodeByCruise, { loading: loadingPackageCode, data: packageCode }] =
    useCruisePackageCode();
  const [execSearchCruise, { loading: loadingCruise, data: cruiseData }] = useCruiseInfo();

  const container = window !== undefined ? () => window.document.body : undefined;
  const flightSearchData = {
    arrivalAirportCode: null,
    bookingID: null,
    currency: null,
  };

  const searchFormMenuItems = [
    {
      id: 'flight-type-selector',
      type: 'dropDown',
      disabled: false,
      items: [
        {
          name: getDictionaryValue(Dictionary.airFlightTypeRoundtrip.key),
          key: getDictionaryValue(Dictionary.airFlightTypeRoundtrip.key),
        },
      ],
    },
  ];

  if (launchDarklyFlags?.ldFlags[ENABLE_ONE_WAY_FLAG]) {
    searchFormMenuItems[0].items.push({
      name: getDictionaryValue(Dictionary.airFlightTypeOneWay.key),
      key: getDictionaryValue(Dictionary.airFlightTypeOneWay.key),
    });
  }

  const [selectedFlightType, setSelectedFlightType] = useState(
    getSessionStorageItem(SESSION_STORAGE_FLIGHT_TYPE_CODE),
  );

  const [
    executeBrowseFlightSearchQuery,
    {
      loading: browseFlightSearchQueryLoading,
      error: browseFlightSearchQueryError,
      data: browseFlightSearchQueryData,
    },
  ] = useBrowseFlightsSearch();

  const [
    executeSearchReturnQuery,
    {
      called: flightsSearchReturnCalled,
      loading: flightsSearchReturnLoading,
      data: flightsSearchReturnData,
      error: flightsSearchReturnError,
    },
  ] = useFlightSearchReturn();

  const handleDrawerToggle = () => {
    setCanOpenFiltersDrawer(!canOpenFiltersDrawer);
  };

  const handleGuestsCounter = (count) => {
    setNumberOfGuestsCounter(count);
    const tempBrowseParam = { ...browseParams };
    tempBrowseParam.searchCriteria.numberOfGuests = count;
    setBrowseParams(tempBrowseParam);
  };

  const submitFlightSearch = (queryparams) => {
    setClearResults(false);
    setShowNoResultsError(false);
    const tempBrowseParam = { ...browseParams };
    tempBrowseParam.searchCriteria.startDate = flightDatesBrowse.startDate;
    tempBrowseParam.searchCriteria.endDate = flightDatesBrowse.endDate;
    tempBrowseParam.searchCriteria.arrivalAirportCode = queryparams?.variables?.arrivalAirportCode;

    setBrowseParams(tempBrowseParam);
    executeBrowseFlightSearchQuery({
      variables: {
        arrivalAirportCode: queryparams?.variables?.arrivalAirportCode,
        // Known limitation (AM-25992): browse has no booking profile, so it cannot tell an open-jaw
        // sailing apart and always pins the return leg to the arrival airport. On an open-jaw sailing
        // the return then departs from the embark gateway instead of the disembark one.
        returnDepartureAirportCode: queryparams?.variables?.arrivalAirportCode,
        numberOfGuests: numberOfGuestsCounter,
        departureAirportCode,
        shipCode: shipCodeState,
        sailDate: departureDateState,
        departureDate: flightDatesBrowse.endDate,
        arrivalDate: flightDatesBrowse.startDate,
        type: queryparams.variables.type,
      },
    });
  };

  const clearSearchResults = () => {
    setSearchResults({
      flightFilters: null,
      matchingFlights: [],
    });
    setSessionStorageItem(SESSION_STORAGE_LAST_DEPARTURE_FLIGHT_RESULTS, null);
    setShowNoResultsError(false);
  };

  function getFlightTitle(typeFlight) {
    if (typeFlight === FLIGHT_SEARCH_TYPE.RETURN) {
      return getDictionaryValue(Dictionary.airReturnFlightTitle.key);
    }
    return getDictionaryValue(Dictionary.airSelectFlightTitle.key);
  }

  const { width } = window.screen;
  const isDesktop = width > 767;

  useEffect(() => {
    if (isDesktop) setExpanded(true);
    if (!isDesktop) setExpanded(false);
  }, [isDesktop]);

  useEffect(() => {
    setBookingProfile({
      bookingToken: null,
      airports: { nodes: [] },
      flightProfile: null,
    });
    setBookingToken(null);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setCanOpenFiltersDrawer(false);
    // eslint-disable-next-line
  }, [shouldHideFiltersDrawer]);

  useEffect(() => {
    if (browseParams && browseParams.searchCriteria) {
      const { shipCode } = browseParams.searchCriteria;
      setShipCodeState(shipCode);
    }
    if (browseParams && browseParams.airports) {
      setAirports(browseParams.airports.nodes);
    }
    // eslint-disable-next-line
  }, [browseParams]);

  useEffect(() => {
    if (searchResults && searchResults.flightType === flightType) {
      if (flightType === FLIGHT_SEARCH_TYPE.RETURN) {
        trackReturnResultsView();
      }
    }

    if (searchResults && searchResults.matchingFlights && searchResults.matchingFlights.length) {
      const { matchingFlights } = searchResults;
      const { flightSegments } = matchingFlights[0];
      const firstSegment = flightSegments[0];
      const lastSegment = flightSegments.slice(-1)[0];

      let airport;
      let departureAir;
      if (flightType === FLIGHT_SEARCH_TYPE.RETURN) {
        airport = firstSegment.departure.airport;
        departureAir = lastSegment.arrival.airport.code;
      } else {
        airport = lastSegment.arrival.airport;
        departureAir = firstSegment.departure.airport.code;
      }

      const { city, code } = airport;
      setNearestAirport(`${city} (${code})`);
      setDepartureAirportCode(departureAir);

      // Clear results on initial load for deeplink
      if (clearResults && urlParams) {
        setSearchResults({
          ...searchResults,
          matchingFlights: [],
          flightFilters: null,
        });
        setSessionStorageItem(SESSION_STORAGE_LAST_DEPARTURE_FLIGHT_RESULTS, null);
      }
    }
    if (searchResults && searchResults.shipBrowseFlight) {
      const { sailing, validDates } = searchResults.shipBrowseFlight;
      setDepartureDateState(sailing.sailDate);
      setArrivalDateState(sailing.returnDate);
      setMinDate(validDates.from);
      setMaxDate(validDates.to);
    }

    return () => {
      if (urlParams) {
        setSessionStorageItem(SESSION_STORAGE_LAST_BROWSE_PARAMS, null);
      }
    };
  }, [searchResults]);

  useEffect(() => {
    if (flightType === FLIGHT_SEARCH_TYPE.DEPARTURE) {
      trackDepartureResultsView();
      if (
        getSessionStorageItem(SESSION_STORAGE_NAVIGATION_HISTORY_LAST_ENTRY) === '' ||
        getSessionStorageItem(SESSION_STORAGE_NAVIGATION_HISTORY_LAST_ENTRY) === undefined
      ) {
        const navigationHistoryLastEntry = document.referrer;
        setSessionStorageItem(
          SESSION_STORAGE_NAVIGATION_HISTORY_LAST_ENTRY,
          navigationHistoryLastEntry,
        );
      }
    }
    // eslint-disable-next-line
  }, [flightType]);

  useEffect(() => {
    if (
      flightType === FLIGHT_SEARCH_TYPE.RETURN &&
      selectedDeparture &&
      searchResults &&
      !browseFlightSearchQueryData &&
      // One-shot guard: storing the return results below mutates searchResults, which re-runs
      // this effect — without this, the return search re-fires in an infinite loop (Apollo's
      // cache used to absorb the repeats; the REST adapter hits the network every time).
      !flightsSearchReturnCalled
    ) {
      const guests = getGuests(bookingProfileVar, searchParamsVar);
      const bookingID = bookingProfileVar()?.flightProfile?.id;
      const queryParams = {
        variables: {
          correlationID: searchResults.correlationID,
          hashKey: selectedDeparture.cabin.hashKey,
          cabinCode: selectedDeparture.cabin.cabinClass.code,
          guests,
          bookingID,
        },
      };

      executeSearchReturnQuery(queryParams);
    }
    // eslint-disable-next-line
  }, [searchResults, flightType, selectedDeparture]);

  useEffect(() => {
    if (
      flightsSearchReturnCalled &&
      !flightsSearchReturnLoading &&
      flightsSearchReturnData?.returnFlightsSearch
    ) {
      const tempSearchResults = { ...searchResults };
      tempSearchResults.matchingFlights =
        flightsSearchReturnData?.returnFlightsSearch?.matchingFlights;
      tempSearchResults.flightFilters = flightsSearchReturnData?.returnFlightsSearch?.flightFilters;
      setSearchResults({ ...tempSearchResults, flightType: FLIGHT_SEARCH_TYPE.RETURN }, true, true);
      setLastSearchDate(new Date());
    }
    // eslint-disable-next-line
  }, [flightsSearchReturnData]);

  useEffect(() => {
    if (
      !browseFlightSearchQueryLoading &&
      !browseFlightSearchQueryError &&
      browseFlightSearchQueryData
    ) {
      setSearchResults(
        {
          ...browseFlightSearchQueryData.browseFlightsSearch,
          flightType: FLIGHT_SEARCH_TYPE.DEPARTURE,
        },
        true,
      );
      setLastSearchDate(new Date());
      if (flightType === FLIGHT_SEARCH_TYPE.RETURN) {
        navigate(`${urlPath}${NAVIGATION_PATHS.browseDeparture}`);
      }
    }
    // eslint-disable-next-line
  }, [browseFlightSearchQueryLoading, browseFlightSearchQueryError, browseFlightSearchQueryData]);

  useEffect(() => {
    if (flightsSearchReturnError || browseFlightSearchQueryError) {
      const error = browseFlightSearchQueryError || flightsSearchReturnError;
      const getErrorByCode = (errorCode) =>
        error.graphQLErrors.find(({ extensions }) => extensions.code === errorCode);
      if (
        Array.isArray(error.graphQLErrors) &&
        (getErrorByCode('ACE-FS-1006') || getErrorByCode('ACE-FS-1028'))
      ) {
        const tempSearchResult = { ...searchResults };
        tempSearchResult.matchingFlights = [];
        tempSearchResult.flightFilters = null;
        setSearchResults(tempSearchResult);
        setSessionStorageItem(SESSION_STORAGE_LAST_DEPARTURE_FLIGHT_RESULTS, null);
        setErrorBannerTitle(getDictionaryValue(Dictionary.airBookFlightNoResultsTitle.key));
        setErrorBannerMessage(
          `${
            getErrorByCode('ACE-FS-1028')
              ? getDictionaryValue(Dictionary.airFlightSearchErrorDescription.key)
              : getDictionaryValue(Dictionary.airNoResultsErrorDescription.key)
          } ${launchDarklyFlags?.ldFlags[SUPPORT_PHONE_NUMBER_LD_FLAG]} ${getDictionaryValue(
            Dictionary.airFlightSearchErrorAssistance.key,
          )} `,
        );
        setShowNoResultsError(true);
      } else if (
        Array.isArray(error.graphQLErrors) &&
        error.graphQLErrors.find(({ message }) => message.startsWith('504'))
      ) {
        setShowNoResultsError(true);
      } else {
        setIsInternalServerError(true);
      }
    }
    // eslint-disable-next-line
  }, [browseFlightSearchQueryError, flightsSearchReturnError]);

  useEffect(() => {
    const noFlightsResult = getSessionStorageItem(SESSION_STORAGE_NO_FLIGHTS_RESULTS_ERROR);
    if (noFlightsResult) setShowNoResultsError(true);
    else setShowNoResultsError(false);
  }, []);

  const guests = Array.from({ length: numberOfGuestsCounter }, () => ({
    flightPassengerType: 'ADULT',
  }));

  useEffect(() => {
    if (shipCodeState && departureDateState) {
      execSearchPackageCodeByCruise({
        variables: {
          sailDate: departureDateState,
          shipCode: shipCodeState,
        },
      });
    }
  }, [shipCodeState, departureDateState]);

  useEffect(() => {
    if (packageCode?.searchPackageCodeByCruiseInfo) {
      const cruiseId = `${packageCode.searchPackageCodeByCruiseInfo}_${departureDateState}`;
      execSearchCruise({ variables: { cruiseId } });
    }
  }, [packageCode]);

  const firstSailing = cruiseData?.cruise?.sailings?.[0];
  const itinerary = {
    ...firstSailing?.itinerary,
    ship: {
      name: browseParams?.ship?.label,
    },
  };
  const isLoading =
    flightsSearchReturnLoading ||
    browseFlightSearchQueryLoading ||
    loadingPackageCode ||
    loadingCruise;

  return (
    <>
      {firstSailing?.itinerary && (
        <ItineraryDetails
          sailing={{
            itinerary,
            sailDate: departureDateState,
            sailTime: searchResults?.shipBrowseFlight?.sailing?.sailTime,
          }}
          flightProfile={{ guests }}
          stretchLayout
        />
      )}

      <div className={classes.resultsContainer}>
        <>
          <AirLoader
            open={isLoading}
            status={isLoading && getDictionaryValue(Dictionary.loaderSearchFlights.key)}
          />
          <div className={classes.progressLineContainer}>
            <ProgressLineItems selectedFlightType={selectedFlightType} extended />
          </div>
          {isInternalServerError ? (
            <InternalError />
          ) : (
            <Grid container className={classes.gridContainerBrowse}>
              {showNoResultsError && (
                <div className={classes.customBanner}>
                  <div className={styles.notification}>
                    <NotificationBannerComponent
                      title={errorBannerTitle}
                      description={errorBannerMessage}
                      type="error"
                      renderCloseBtn
                      isBannerOpen
                      onClose={() => setShowNoResultsError(false)}
                    />
                  </div>
                </div>
              )}
              <Grid item md={12} lg={12}>
                <div className={classes.resultsContainerBrowse}>
                  {selectedDeparture && flightType === 'return' && (
                    <ReviewCard
                      isNewDeparting
                      isSummary
                      isDesktop={expanded}
                      handleAccordion={() => setExpanded(!expanded)}
                      cardTitle={getDictionaryValue(Dictionary.airDepartingFlightLabel.key)}
                    />
                  )}
                  <div
                    className={
                      selectedDeparture && flightType === 'return'
                        ? `${classes.newBrowseDepartingSection} flight-results-secoundary-section`
                        : classes.newBrowseDepartingSection
                    }>
                    <Typography variant="h2" className="title">
                      {getFlightTitle(flightType)}
                    </Typography>
                    <InfoTooltip
                      description={getDictionaryValue(Dictionary.airSelectFlightDescription.key)}
                    />
                  </div>
                  <div className={classes.resultsWrapperBrowse}>
                    <Grid item lg={4} xl={3}>
                      <Card elevation={1} className={classes.filterSectionBrowse}>
                        <FiltersPanel />
                      </Card>
                    </Grid>
                    {canOpenFiltersDrawer && (
                      <Header
                        position="fixed"
                        elevation={0}
                        className={classes.filterPanelHeaderBrowse}>
                        <NavLink component="a" onClick={handleDrawerToggle}>
                          <Typography variant="body2" className="headerTypography">
                            {getDictionaryValue(Dictionary.airBackLabel.key)}
                          </Typography>
                        </NavLink>
                      </Header>
                    )}
                    <Drawer
                      container={container}
                      anchor="left"
                      variant="temporary"
                      onClose={handleDrawerToggle}
                      classes={{
                        paper: classes.filtersPanelDrawerBrowse,
                      }}
                      open={canOpenFiltersDrawer}
                      ModalProps={{
                        keepMounted: true,
                      }}>
                      <FiltersPanel />
                    </Drawer>
                    <Grid
                      item
                      xs={12}
                      lg={8}
                      xl={9}
                      className={
                        filteredResults && filteredResults.length === 0
                          ? classes.centeredBrowse
                          : ''
                      }>
                      {flightType !== FLIGHT_SEARCH_TYPE.RETURN && (
                        <div className={classes.wrapNewFormSection}>
                          <Card elevation={3} className={clsx(classesResults.searchWrapper)}>
                            <CardContent className={classes.searchWrapperCardContentBrowse}>
                              {airports?.length && departureDateState && arrivalDateState && (
                                <FlightSearchForm
                                  setDepartureAirportCode={setDepartureAirportCode}
                                  isFromBrowseSearch
                                  className={classesResults.searchFlightsPageForm}
                                  submitFlightSearch={submitFlightSearch}
                                  arrivalAirportCode={flightSearchData.arrivalAirportCode}
                                  bookingID={flightSearchData.bookingID}
                                  currency={flightSearchData.currency}
                                  incomingStartDate={departureDateState}
                                  incomingEndDate={arrivalDateState}
                                  flightToDefault={nearestAirport}
                                  minAvailableDate={minDate}
                                  maxAvailableDate={maxDate}
                                  acOptions={airports}
                                  buttonInLine={false}
                                  labelTo={getDictionaryValue(Dictionary.airFlyingToLabel.key)}
                                  labelFrom={getDictionaryValue(Dictionary.airFromLabel.key)}
                                  labelButton={renderContent(isXlDictionariesKey, isXlDictionaries)}
                                  noResultsErrorMessage={getDictionaryValue(
                                    Dictionary.airNoResultsErrorMessage.key,
                                  )}
                                  labelDatepicker={getDictionaryValue(Dictionary.airDatesLabel.key)}
                                  placeholderFrom={getDictionaryValue(
                                    Dictionary.airFromPlaceholder.key,
                                  )}
                                  menuItems={searchFormMenuItems}
                                  showPassengersCounter
                                  handleGuestsCounter={handleGuestsCounter}
                                  numberOfGuestCounter={numberOfGuestsCounter}
                                  setFlightDatesBrowse={setFlightDatesBrowse}
                                  setSelectedFlightType={setSelectedFlightType}
                                  clearSearchResults={clearSearchResults}
                                />
                              )}
                            </CardContent>
                          </Card>
                        </div>
                      )}
                      {filteredResults && filteredResults.length > 0 ? (
                        <div className={classes.wrapResultsSection}>
                          <ResultsSuccess
                            handleDrawerToggle={handleDrawerToggle}
                            lastSearchDate={lastSearchDate}
                            flightType={flightType}
                          />
                        </div>
                      ) : (
                        <>
                          {getSessionStorageItem(SESSION_STORAGE_LAST_DEPARTURE_FLIGHT_RESULTS)
                            ?.matchingFlights &&
                            !isLoading && <ResultsFail className={classes.mobileNoResultsBrowse} />}
                          {searchResults && !searchResults.noResultsError && (
                            <Button
                              variant="contained"
                              color="primary"
                              className={classes.mobileClearFilterButtonBrowse}
                              onClick={clearFilters}>
                              {getDictionaryValue(Dictionary.airClearAllFiltersLabel.key)}
                            </Button>
                          )}
                        </>
                      )}
                    </Grid>
                  </div>
                </div>
              </Grid>
            </Grid>
          )}
        </>
      </div>
    </>
  );
};

export default BrowseResults;
